import type { NavModule, Route } from '../../lib/router';

/**
 * The navigation map, declared once.
 *
 * The desktop menu bar, the mobile drawer and the secondary tab strip are all
 * projections of this table, so a new screen cannot appear in one navigation
 * surface and go missing from another. Labels are `wp`-namespace keys.
 */

export interface NavLink {
  id: string;
  label_key: string;
  route: Route;
}

export interface NavMenuSection {
  /** Optional column heading — the Payroll menu splits into two columns. */
  heading_key?: string;
  items: NavLink[];
}

export interface NavEntry {
  id: NavModule;
  label_key: string;
  /** Where the top-level item navigates when clicked directly. */
  route: Route;
  /** Dropdown contents. An empty list makes the item a plain link. */
  sections: NavMenuSection[];
}

function profile_link(
  id: string,
  label_key: string,
  tab: Extract<Route, { view: 'profile' }>['tab'],
): NavLink {
  return {
    id,
    label_key,
    route: { module: 'employees', view: 'profile', employee_id: null, tab },
  };
}

export const NAV_ENTRIES: NavEntry[] = [
  {
    id: 'dashboard',
    label_key: 'nav.dashboard',
    route: { module: 'dashboard' },
    sections: [],
  },
  {
    id: 'employees',
    label_key: 'nav.employees',
    route: { module: 'employees', view: 'directory' },
    sections: [],
  },
  {
    id: 'leave',
    label_key: 'nav.leave',
    route: { module: 'leave', tab: 'pending' },
    sections: [],
  },
  {
    id: 'timesheets',
    label_key: 'nav.timesheets',
    route: { module: 'timesheets' },
    sections: [],
  },
  {
    id: 'payroll',
    label_key: 'nav.payroll',
    route: { module: 'payroll', tab: 'run' },
    sections: [
      {
        items: [
          {
            id: 'run',
            label_key: 'nav.payroll_run',
            route: { module: 'payroll', tab: 'run' },
          },
          {
            id: 'history',
            label_key: 'nav.payroll_history',
            route: { module: 'payroll', tab: 'history' },
          },
          {
            id: 'reports',
            label_key: 'nav.payroll_reports',
            route: { module: 'payroll', tab: 'reports' },
          },
          {
            id: 'policies',
            label_key: 'nav.payroll_policies',
            route: { module: 'payroll', tab: 'policies' },
          },
        ],
      },
    ],
  },
  {
    id: 'settings',
    label_key: 'nav.settings',
    route: { module: 'settings', tab: 'organisation' },
    sections: [],
  },
];

/** Secondary tab strips, keyed by the module that owns them. */
const SUB_TABS: Partial<Record<NavModule, NavLink[]>> = {
  employees: [
    {
      id: 'directory',
      label_key: 'nav.employees_directory',
      route: { module: 'employees', view: 'directory' },
    },
    profile_link('profile', 'nav.employees_profile', 'job'),
  ],
  payroll: [
    {
      id: 'run',
      label_key: 'nav.payroll_run',
      route: { module: 'payroll', tab: 'run' },
    },
    {
      id: 'history',
      label_key: 'nav.payroll_history',
      route: { module: 'payroll', tab: 'history' },
    },
    {
      id: 'reports',
      label_key: 'nav.payroll_reports',
      route: { module: 'payroll', tab: 'reports' },
    },
    {
      id: 'policies',
      label_key: 'nav.payroll_policies',
      route: { module: 'payroll', tab: 'policies' },
    },
  ],
};

export function sub_tabs_for(route: Route): NavLink[] {
  return SUB_TABS[route.module] ?? [];
}

/** Which secondary tab the current route sits under. */
export function active_sub_tab_id(route: Route): string | null {
  switch (route.module) {
    case 'employees':
      return route.view === 'directory' ? 'directory' : 'profile';
    case 'payroll':
      return route.tab;
    case 'dashboard':
    case 'expenses':
    case 'leave':
    case 'timesheets':
    case 'update_requests':
    case 'settings':
    case 'my_pay':
    case 'my_leave':
    case 'my_profile':
      return null;
  }
}

/** The employee (ESS) surface's pages, in bottom-tab-bar order. */
export type EssTab = 'my_pay' | 'my_leave' | 'my_profile';

/**
 * Which of the employee (ESS) pages the current route shows.
 *
 * The single source of truth for that question, because it has two consumers
 * that MUST agree: `App.tsx`'s phone/tablet `<Tabs>` strip and
 * `EmployeeTopBar`'s desktop in-bar links. They are never visible at the same
 * time (one is `lg:hidden`, the other `hidden lg:flex`), which is exactly why
 * a divergence would go unnoticed — the drift failure mode
 * `docs/design-system/CLAUDE.md` § "One canonical component per status/badge
 * concept" describes.
 *
 * `my_leave` and `my_profile` map to themselves; everything else is My Pay —
 * including the `dashboard` route an unparseable hash falls back to, and any
 * employer module an employer in the native shell might reach by URL.
 * `App.tsx` renders the screen from this same value (anything else renders
 * `MyPayScreen`), so the highlight always matches what is actually on
 * screen. An earlier version of the in-bar links compared
 * `route.module === 'my_pay'` instead, which marked NEITHER link current on
 * those fallback routes.
 */
export function active_ess_tab(route: Route): EssTab {
  if (route.module === 'my_leave' || route.module === 'my_profile') {
    return route.module;
  }
  return 'my_pay';
}

export function is_module_active(route: Route, module: NavModule): boolean {
  return route.module === module;
}
